import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import AdminPagination from '../../components/AdminPagination';
import './Messages.css';

const MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const Messages = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [messagesError, setMessagesError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [mobileShowList, setMobileShowList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const isAdmin = user?.role === 'main_admin' || user?.role === 'admin';

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [searchApplied, timeFilter, dateFrom, dateTo, restaurantId]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchVenues = async () => {
      try {
        const [rRes, sRes] = await Promise.all([
          axios.get(`${API_URL}/admin/restaurants`),
          axios.get(`${API_URL}/admin/stores`)
        ]);
        setRestaurants(rRes.data);
        setStores(sRes.data);
      } catch (e) {
        console.error('Error fetching venues:', e);
      }
    };
    fetchVenues();
  }, [isAdmin]);

  useEffect(() => {
    if (selectedOrder) {
      setMessagesError(null);
      fetchMessages(selectedOrder._id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedOrder._id);
      }, 5000);
      return () => clearInterval(interval);
    } else {
      setMessages([]);
      setMessagesError(null);
    }
  }, [selectedOrder]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fetchOrders = async () => {
    setOrdersError(null);
    try {
      const params = {};
      if (searchApplied) params.search = searchApplied;
      if (timeFilter) params.timeFilter = timeFilter;
      if (timeFilter === 'custom' && dateFrom && dateTo) {
        params.dateFrom = dateFrom;
        params.dateTo = dateTo;
      }
      if (restaurantId) params.restaurantId = restaurantId;
      const response = await axios.get(`${API_URL}/messages/orders`, { params });
      setOrders(response.data || []);
      const orderIdFromState = location.state?.orderId;
      if (orderIdFromState && response.data.length > 0) {
        const found = response.data.find(o => o._id === orderIdFromState);
        if (found) {
          setSelectedOrder(found);
        } else if (!selectedOrder) {
          setSelectedOrder(response.data[0]);
        }
      } else if (response.data.length > 0 && !selectedOrder) {
        setSelectedOrder(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      const errMsg = error.response?.data?.message || error.message || (t('admin.loadError') || 'Failed to load orders');
      setOrdersError(errMsg);
      showError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    setSearchApplied(search.trim());
    setCurrentPage(1);
  };

  const clearMessagesFilters = () => {
    setSearch('');
    setSearchApplied('');
    setTimeFilter('');
    setDateFrom('');
    setDateTo('');
    setRestaurantId('');
    setCurrentPage(1);
  };

  const fetchMessages = async (orderId) => {
    setMessagesError(null);
    try {
      const response = await axios.get(`${API_URL}/messages/order/${orderId}`);
      setMessages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      const errMsg = error.response?.data?.message || error.message || (t('admin.loadError') || 'Failed to load messages');
      setMessagesError(errMsg);
      showError(errMsg);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/messages/order/${selectedOrder._id}`,
        { message: newMessage.trim() }
      );
      setMessages([...messages, response.data]);
      setNewMessage('');
      // Update unread count for this order
      setOrders(orders.map(order => 
        order._id === selectedOrder._id 
          ? { ...order, unreadCount: 0 }
          : order
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      showError(t('admin.messageFailed'));
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#f39c12', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      delivered: { bg: '#27ae60', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getRoleLabel = (role) => {
    const labels = {
      main_admin: t('admin.mainAdmin') || 'Main Admin',
      admin: t('admin.roleAdmin') || 'Admin',
      invoicer: t('admin.invoicer') || 'Invoicer',
      restaurant_owner: t('admin.restaurantOwner') || 'Restaurant Owner',
      restaurant_user: t('admin.restaurantUser') || 'Restaurant User',
      store_owner: t('admin.storeOwner') || 'Store Owner',
      store_user: t('admin.storeUser') || 'Store User',
      delivery_driver: t('admin.deliveryDriver') || 'Driver',
      customer: t('admin.customer') || 'Customer'
    };
    return labels[role] || role;
  };

  const statusOptions = [
    { value: 'all', label: t('admin.all') },
    { value: 'pending', label: t('admin.pending') },
    { value: 'confirmed', label: t('admin.confirmed') },
    { value: 'preparing', label: t('admin.preparing') },
    { value: 'outForDelivery', label: t('admin.outForDelivery') },
    { value: 'delivered', label: t('admin.delivered') },
    { value: 'cancelled', label: t('admin.cancelled') }
  ];

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(start, start + itemsPerPage);
  }, [filteredOrders, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrders.length, itemsPerPage]);

  if (loading) {
    return (
      <div className="messages-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  const venueOptions = [
    ...restaurants.map(r => ({ id: r._id, name: r.name, type: 'Restaurant' })),
    ...stores.map(s => ({ id: s._id, name: s.name, type: 'Store' }))
  ];

  return (
    <div className="messages-container">
      <div className="messages-header">
        <h2>{t('admin.messages')}</h2>
        <div className="status-filter-buttons">
          {statusOptions.map(status => (
            <button
              key={status.value}
              className={`status-filter-btn ${statusFilter === status.value ? 'active' : ''}`}
              onClick={() => setStatusFilter(status.value)}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="messages-filters">
          <div className="messages-filters-row">
            <div className="filter-group messages-search-group">
              <label>{t('admin.search')}</label>
              <div className="search-controls">
                <input
                  type="text"
                  className="messages-search-input"
                  placeholder={t('admin.searchMessagesPlaceholder') || 'Customer name, order ID...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <button type="button" className="btn btn-primary btn-sm btn-search" onClick={applySearch}>
                  {t('admin.search')}
                </button>
              </div>
            </div>
            <div className="messages-filters-controls">
              <div className="filter-group">
                <label>{t('admin.timePeriod')}</label>
              <select
                value={timeFilter}
                onChange={(e) => { setTimeFilter(e.target.value); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value="">{t('admin.all')}</option>
                <option value="today">{t('admin.today')}</option>
                <option value="yesterday">{t('admin.yesterday')}</option>
                <option value="last7days">{t('admin.last7Days')}</option>
                <option value="last30days">{t('admin.last30Days')}</option>
                <option value="custom">{t('admin.customRange')}</option>
              </select>
            </div>
            {timeFilter === 'custom' && (
              <div className="filter-group date-range-group">
                <label>{t('admin.dateRange')}</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <span>{t('admin.to')}</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom} />
              </div>
            )}
            <div className="filter-group">
              <label>{t('admin.restaurant')}/{t('admin.store')}</label>
              <select
                value={restaurantId}
                onChange={(e) => { setRestaurantId(e.target.value); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value="">{t('admin.all')}</option>
                {venueOptions.map((v) => (
                  <option key={v.id} value={v.id}>{v.type === 'Store' ? '🏪' : '🍽️'} {v.name}</option>
                ))}
              </select>
              </div>
              <div className="filter-group filter-group-clear">
                <label className="filter-group-spacer" htmlFor="messages-clear-filters" aria-hidden="true">&#8203;</label>
                <button id="messages-clear-filters" type="button" className="btn btn-secondary btn-sm btn-clear-filters" onClick={clearMessagesFilters}>
                  {t('admin.clearFilters')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="messages-content">
        {(!isMobile || mobileShowList || !selectedOrder) && (
        <div className="orders-list-panel">
          <h3>{t('admin.orders')} ({filteredOrders.length})</h3>
          {ordersError && (
            <div className="messages-error-banner">
              <i className="fas fa-exclamation-triangle"></i> {ordersError}
            </div>
          )}
          <div className="orders-list">
            {filteredOrders.length === 0 && !ordersError ? (
              <div className="no-orders">{t('admin.noOrdersForMessages')}</div>
            ) : filteredOrders.length > 0 ? (
              paginatedOrders.map(order => (
                <div
                  key={order._id}
                  className={`order-item ${selectedOrder?._id === order._id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedOrder(order);
                    if (isMobile) setMobileShowList(false);
                  }}
                >
                  <div className="order-item-header">
                    <div className="order-number">#{order.orderNumber || order._id.slice(-6)}</div>
                    {getStatusBadge(order.status)}
                  </div>
                  <div className="order-item-info">
                    <div className="order-restaurant">
                      <i className="fas fa-store"></i> {order.restaurantName}
                    </div>
                    <div className="order-customer">
                      <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                    </div>
                    {order.unreadCount > 0 && (
                      <div className="unread-badge">{order.unreadCount}</div>
                    )}
                  </div>
                </div>
              ))
            ) : null}
          </div>
          {filteredOrders.length > 0 && (
            <AdminPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredOrders.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
        )}

        <div className={`conversation-panel ${isMobile && selectedOrder && !mobileShowList ? 'conversation-panel-mobile-active' : ''}`}>
          {!selectedOrder ? (
            <div className="conversation-placeholder">
              <i className="fas fa-comments"></i>
              <p>Select an order to view conversation</p>
            </div>
          ) : (
            <>
              <div className="conversation-header">
                <div className="conversation-header-main">
                  {isMobile && (
                    <button type="button" className="back-to-orders-btn" onClick={() => setMobileShowList(true)} aria-label={t('admin.backToOrdersList')}>
                      <i className="fas fa-arrow-left"></i> {t('admin.backToOrdersList')}
                    </button>
                  )}
                  <div>
                    <h3>Order #{selectedOrder.orderNumber || selectedOrder._id.slice(-6)}</h3>
                    <p>{selectedOrder.restaurantName}</p>
                  </div>
                </div>
                {getStatusBadge(selectedOrder.status)}
              </div>

              {messagesError && (
                <div className="messages-error-banner">
                  <i className="fas fa-exclamation-triangle"></i> {messagesError}
                </div>
              )}
              <div className="messages-list" ref={messagesContainerRef}>
                {messages.map((message, index) => (
                  <div
                    key={message._id || index}
                    className={`message-item ${message.type === 'status_log' ? 'status-log' : ''} ${
                      message.sender?._id === user?._id ? 'sent' : 'received'
                    }`}
                  >
                    {message.type === 'status_log' ? (
                      <div className="status-log-message">
                        <i className="fas fa-info-circle"></i>
                        <span>{message.message}</span>
                        <span className="message-time">{formatDate(message.createdAt)}</span>
                      </div>
                    ) : (
                      <>
                        <div className="message-sender">
                          {getRoleLabel(message.sender?.role || 'customer')}
                          {message.sender?._id === user?._id && ' (You)'}
                        </div>
                        <div className="message-content">
                          {message.message}
                        </div>
                        <div className="message-time">{formatDate(message.createdAt)}</div>
                      </>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form className="message-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('admin.typeMessage')}
                  disabled={sending}
                  className="message-input"
                />
                <button type="submit" disabled={sending || !newMessage.trim()} className="send-button">
                  <i className="fas fa-paper-plane"></i>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
